import React, {useContext, useState} from 'react';
import axios from 'axios';
import config from '../../config.js';
import UserContext from '../common/UserContext.js';

// components
import Alert from '../common/Alert.js';

// kendo react
import {Dialog, DialogActionsBar} from '@progress/kendo-react-dialogs';
import {Button} from '@progress/kendo-react-buttons';

// multilingual
import {Text} from '../common/MultilingualText.js';
import {useLocalization} from '@progress/kendo-react-intl';
import {
    cancelKey,
    areYouSureKey,
    removeKey,
    removeUserKey,
    permanentlyDeleteUserKey,
    genericErrorTitleKey,
    errorOccurredRequestKey,
    hostUserAliasAlreadyExistsKey,
    hostUserHostIdRequiredKey,
    hostUserNotesRequiredKey,
    hostUserEmailRequiredKey,
    hostUserDeleteFailedKey,
    hostUserAliasRequiredKey,
    hostUserInvalidOperationKey,
    hostUserHostEmailExistsKey,
    mainMessages,
} from '../../assets/text/MultilingualText.js';
import {useSupportContext} from "../common/SupportContext";

function RemoveUserModal(props) {
    const hostUserErrorMessageMap = {
        MOD_HOSTUSER_1: hostUserAliasAlreadyExistsKey,
        MOD_HOSTUSER_2: hostUserHostIdRequiredKey,
        MOD_HOSTUSER_3: hostUserNotesRequiredKey,
        MOD_HOSTUSER_4: hostUserEmailRequiredKey,
        MOD_HOSTUSER_5: hostUserDeleteFailedKey,
        MOD_HOSTUSER_6: hostUserAliasRequiredKey,
        MOD_HOSTUSER_7: hostUserInvalidOperationKey,
        MOD_HOSTUSER_8: hostUserHostEmailExistsKey,
    };

    const {
        user,
        setUser,
        getUsers,
        setIsVisible,
        setIsLoading
    } = props;
    const {
        accessToken,
        siteLanguageDefault
    } = useContext(UserContext);
    const { dispatch } = useSupportContext();
    const localization = useLocalization();

    const titleStyle = {
        fontWeight: "normal",
        margin: 0,
        color: 'var(--color-text-primary)'
    };

    const [name] = useState(user.name ? user.name : "");
    const [email] = useState(user.email ? user.email : "");
    const [phuId] = useState(user.phu_id ? user.phu_id : "");
    const [showRequestFailed, setShowRequestFailed] = useState(false);
    const [requestFailedMessageKey, setRequestFailedMessageKey] = useState(errorOccurredRequestKey);

    // closes the remove user modal
    const close = () => {
        setUser({});
        setIsVisible(isVisible => ({...isVisible, removeUser: false}));
        // support metadata push
        dispatch({ type: 'UPDATE_TRANSACTION', payload: {phu_id: ""}});
    };

    /*
    * removeUser() removes the user
    */
    const removeUser = () => {
        let headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + accessToken
        };

        let data = {
            "module": "HOST_USER",
            "sub_module": "",
            "action": "DELETE",
            "input_json": {
                "ids": [phuId]
            }
        };

        setIsLoading(true);

        axios.delete(
            config.users.USERS,
            {headers: headers, data: data}
        )
            .then((response) => {
                if (response.status === 200) {
                    setIsLoading(false);
                    getUsers();
                    close();

                }
            })
            .catch((error) => {
                console.log("ERROR: Failed to DELETE user", error);
                let errorMessageKey = errorOccurredRequestKey;
                if (error.response?.status === 400 && error.response?.hasOwnProperty('data')) {
                    const errorCode = error.response?.data?.error_code?.toUpperCase();
                    errorMessageKey = hostUserErrorMessageMap[errorCode] || errorOccurredRequestKey;
                }

                setRequestFailedMessageKey(errorMessageKey);
                setShowRequestFailed(true);
                setIsLoading(false);
            })
    }

    return (
        <Dialog
            className={"remove-user-modal ksm-dialog"}
            title={<div style={titleStyle}>
                {localization.toLanguageString(removeUserKey, mainMessages[siteLanguageDefault][removeUserKey])}
            </div>}
            onClose={close}
        >
            <div
                className={"k-h5"}
            >
                <Text
                    textkey={areYouSureKey}
                    textdefault={mainMessages[siteLanguageDefault][areYouSureKey]}
                />
            </div>
            <div
                style={{
                    color: "black",
                    fontSize: "1rem"
                }}
            >
                <Text
                    textkey={permanentlyDeleteUserKey}
                    textdefault={mainMessages[siteLanguageDefault][permanentlyDeleteUserKey]}
                />
            </div>
            <div className={"k-h6 k-ml-4 mt-2 mb-0 user-textWrap"}>
                {name} ({email})
            </div>
            {showRequestFailed && (
                <div className={'mt-2'}>
                    <Alert
                        type={'error'}
                        title={localization.toLanguageString(genericErrorTitleKey, mainMessages[siteLanguageDefault][genericErrorTitleKey])}
                        message={localization.toLanguageString(requestFailedMessageKey, mainMessages[siteLanguageDefault][requestFailedMessageKey])}
                        showHandler={() => setShowRequestFailed(false)}
                    />
                </div>
            )}
            <DialogActionsBar layout="center">
                <Button
                    themeColor={"primary"}
                    size={"large"}
                    fillMode={"outline"}
                    type={"button"}
                    onClick={close}
                >
                    {localization.toLanguageString(cancelKey, mainMessages[siteLanguageDefault][cancelKey])}
                </Button>
                <Button
                    themeColor={"primary"}
                    size={"large"}
                    fillMode={"solid"}
                    type={"button"}
                    onClick={removeUser}
                >
                    {localization.toLanguageString(removeKey, mainMessages[siteLanguageDefault][removeKey])}
                </Button>
            </DialogActionsBar>
        </Dialog>
    )
}

export default RemoveUserModal;